import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import DeleteModal from '../../Components/Common/DeleteModal';
import ActionButton from '../../Components/Common/ActionButton';
import { FormGrid, TextField } from '../../Components/Form';
import ListPager from '../../Components/Common/ListPager';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../Components/Common/ListFilters';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';

const RolesList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [modal, setModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalRole, setModalRole] = useState(false);
  const [roleFormData, setRoleFormData] = useState({
    name: null,
    normalizedName: null,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(`${api.API_URL}/role/${invoice.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Role deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token, search);
      } catch (err) {
        notify.apiError(err, 'delete role');
      }
    }
  };

  const deleteMultiple = () => {
    // Implement multiple delete functionality
    // This would typically involve deleting selected items
    // For now, just close the modal
    setDeleteModalMulti(false);
  };

  const toggleRoleModal = () => {
    setModal(!modal);
  };

  const columns = [
    {
      id: 'name',
      name: 'Role Name',
      selector: row => row.name,
      sortable: true,
      width: '40%',
      cell: row => (
        <span className='to-title' title={String(row.name ?? '')}>
          {row.name ?? '-'}
        </span>
      ),
    },

    {
      id: 'normalizedName',
      name: 'Normalized Name',
      selector: row => row.normalizedName,
      sortable: true,
      width: '45%',
      cell: row => (
        <span title={String(row.normalizedName ?? '')}>
          {row.normalizedName ?? '-'}
        </span>
      ),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Roles';

  const handleChange = e => {
    const { name, value } = e.target;
    setRoleFormData({ ...roleFormData, [name]: value });
  };

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search);
  }, [currentPage, rowsPerPage, token, search]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: clearFilter,
    },
  ].filter(Boolean);

  const fetchData = async (currentPage, rowsPerPage, token, search) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    fetch(
      `${api.API_URL}/role?PageNumber=${currentPage}&PageSize=${rowsPerPage}${search ? `&Search=${encodeURIComponent(search)}` : ''}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    )
      .then(data => data.json())
      .then(
        result => {
          if (reqId !== requestRef.current) return; // stale response
          setData({
            items: result.data.items || [],
          });
          setTotalRows(result.data.totalItems || 0);
        },
        _error => {
          // Handle error appropriately
        }
      )
      .catch(_error => {
        // Handle error silently or show appropriate message
      })
      .finally(() => {
        if (reqId === requestRef.current) setLoading(false);
      });
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setRoleFormData({
      name: null,
      normalizedName: null,
      id: null,
    });
    roleModal();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchRoleDetail(mid);
    roleModal();
  };

  const roleModal = () => {
    setModalRole(!modalRole);
  };

  const selectedFieldsRole = {
    // id: roleFormData.id,
    name: roleFormData.name,
    // normalizedName: roleFormData.normalizedName,
  };

  const handleAddDataRole = async () => {
    try {
      await axios.post(`${api.API_URL}/role`, selectedFieldsRole, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Role added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const fetchRoleDetail = async mid => {
    try {
      const response = await axios.get(`${api.API_URL}/role/${mid}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `application/json`,
        },
      });
      const data = response.data?.data ?? response.data ?? {};
      setRoleFormData({
        name: data.name || null,
        normalizedName: data.normalizedName || null,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
    }
  };

  const handleUpdDataRole = async mid => {
    try {
      await axios.put(`${api.API_URL}/role/${mid}`, selectedFieldsRole, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this role?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete role'
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            deleteMultiple();
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{ label: 'Role', to: '/apps-roles/view' }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-shield-user-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Data Roles</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the user roles available in the system.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, search)
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Role
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search roles...'
                      ariaLabel='Search roles'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}\u2013${endEntry} of ${totalRows} roles`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='name'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No role found'
                          emptyHint='No roles have been created yet. Roles group users and decide which menus and permissions they get, so create one first.'
                          filteredHint={`No role matches "${filterText}". Try another keyword.`}
                          hasFilters={filterChips.length > 0}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Role',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalRole}
            role='dialog'
            autoFocus={true}
            centered
            id='addAddressModal'
            toggle={toggleRoleModal}
            data-bs-backdrop='static'
            size='sm'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalRole(!modalRole);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {roleFormData.id !== null ? 'Edit Role' : 'Add Role'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={1}>
                <TextField
                  label='Role name'
                  name='name'
                  placeholder='e.g. Procurement Analyst'
                  value={roleFormData.name || ''}
                  error={errors.name}
                  onChange={e => {
                    handleChange(e);
                    setErrors({
                      ...errors,
                      name: '',
                    });
                  }}
                />
                {roleFormData.id !== null && (
                  <TextField
                    label='Normalized name'
                    name='normalizedName'
                    mode='readonly'
                    value={roleFormData.normalizedName || ''}
                    hint='Generated by the system from the role name'
                  />
                )}
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalRole(!modalRole);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-database-2-line'
                onClick={async () => {
                  await (roleFormData.id !== null
                    ? handleUpdDataRole(roleFormData.id)
                    : handleAddDataRole());
                  setModalRole(false);
                }}
              >
                {roleFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default RolesList;
